/**
 * Flor de Damas — theme interactions.
 * Vanilla JS, no dependencies. Everything degrades to a working
 * (visible, submittable) page when this file never loads.
 */
(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------ */
  /* Scroll reveals                                                */
  /* ------------------------------------------------------------ */
  const initReveals = () => {
    const targets = document.querySelectorAll('[data-reveal]');
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('revealed'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    targets.forEach((el) => {
      // Anything already in the viewport on load reveals immediately,
      // staggered by its order within the closest stagger group.
      const group = el.closest('[data-reveal-group]');
      if (group) {
        const siblings = Array.from(group.querySelectorAll('[data-reveal]'));
        const index = siblings.indexOf(el);
        if (index > 0) el.style.setProperty('--reveal-delay', `${Math.min(index * 80, 480)}ms`);
      }
      observer.observe(el);
    });
  };

  /* ------------------------------------------------------------ */
  /* Header: solid after scroll                                    */
  /* ------------------------------------------------------------ */
  const initHeader = () => {
    const header = document.querySelector('.site-header');
    if (!header) return;

    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'position:absolute;top:0;height:48px;width:1px;pointer-events:none;';
    document.body.prepend(sentinel);

    if (!('IntersectionObserver' in window)) {
      header.classList.add('site-header--solid');
      return;
    }

    new IntersectionObserver(([entry]) => {
      header.classList.toggle('site-header--solid', !entry.isIntersecting);
    }).observe(sentinel);
  };

  /* ------------------------------------------------------------ */
  /* Header inline search: icon expands a field leftward.          */
  /* Without JS the icon submits straight to the search page.      */
  /* ------------------------------------------------------------ */
  const initHeaderSearch = () => {
    const form = document.querySelector('[data-header-search]');
    if (!form || form.dataset.searchReady) return;
    form.dataset.searchReady = 'true';

    const input = form.querySelector('input[type="search"]');
    const toggle = form.querySelector('.header-search__toggle');
    if (!input || !toggle) return;

    const setOpen = (open) => {
      form.classList.toggle('header-search--open', open);
      toggle.setAttribute('aria-expanded', String(open));
      input.tabIndex = open ? 0 : -1;
      if (open) input.focus();
    };

    toggle.addEventListener('click', (event) => {
      const isOpen = form.classList.contains('header-search--open');
      if (!isOpen) {
        event.preventDefault();
        setOpen(true);
      } else if (!input.value.trim()) {
        event.preventDefault();
        setOpen(false);
      }
      // Open with a query: let the form submit.
    });

    form.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && form.classList.contains('header-search--open')) {
        setOpen(false);
        toggle.focus();
      }
    });

    document.addEventListener('click', (event) => {
      if (!form.contains(event.target)) setOpen(false);
    });
  };

  /* ------------------------------------------------------------ */
  /* Drawers built on <details scroll-lock>                        */
  /* ------------------------------------------------------------ */
  const initDrawers = () => {
    document.querySelectorAll('details[data-drawer]').forEach((drawer) => {
      drawer.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && drawer.open) {
          drawer.open = false;
          const summary = drawer.querySelector('summary');
          if (summary) summary.focus();
        }
      });

      // Close when a link inside is followed (same-page anchors included).
      drawer.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => {
          drawer.open = false;
        });
      });
    });
  };

  /* ------------------------------------------------------------ */
  /* Subscribe panel: the chosen plan drives the CTA link          */
  /* ------------------------------------------------------------ */
  const initSubscribePanels = () => {
    document.querySelectorAll('[data-subscribe]').forEach((panel) => {
      const cta = panel.querySelector('[data-subscribe-cta]');
      if (!cta) return;

      const update = () => {
        const checked = panel.querySelector('input[type="radio"]:checked');
        const url = checked && checked.dataset.planUrl;
        if (url) cta.href = url;
      };

      panel.querySelectorAll('input[type="radio"]').forEach((radio) => {
        radio.addEventListener('change', update);
      });
      update();
    });
  };

  /* ------------------------------------------------------------ */
  /* Quantity steppers                                             */
  /* ------------------------------------------------------------ */
  const initQuantities = () => {
    document.querySelectorAll('.quantity').forEach((wrapper) => {
      const input = wrapper.querySelector('input');
      if (!input) return;

      wrapper.querySelectorAll('button[data-step]').forEach((button) => {
        button.addEventListener('click', () => {
          const step = Number(button.dataset.step);
          const min = Number(input.min || 0);
          const next = Math.max(min, (Number(input.value) || 0) + step);
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  };

  /* ------------------------------------------------------------ */
  /* Cart page: auto-submit quantity changes                       */
  /* ------------------------------------------------------------ */
  const initCart = () => {
    const cartForm = document.querySelector('form[data-cart-form]');
    if (!cartForm) return;

    cartForm.addEventListener('change', (event) => {
      if (event.target.matches('input[name="updates[]"]')) {
        cartForm.requestSubmit();
      }
    });
  };

  /* ------------------------------------------------------------ */
  /* Product form: variant selection                               */
  /* ------------------------------------------------------------ */
  const initProductForms = () => {
    document.querySelectorAll('[data-product-form]').forEach((root) => {
      const dataEl = root.querySelector('[data-variant-json]');
      if (!dataEl) return;

      let variants;
      try {
        variants = JSON.parse(dataEl.textContent);
      } catch (error) {
        return;
      }

      const idInput = root.querySelector('input[name="id"]');
      const priceEls = root.querySelectorAll('[data-price]');
      const comparePriceEls = root.querySelectorAll('[data-compare-price]');
      const buyButtons = root.querySelectorAll('[data-add-to-cart]');
      const moneyFormat = root.dataset.moneyFormat || '${{amount}}';

      const formatMoney = (cents) => {
        const amount = (cents / 100).toFixed(2);
        return moneyFormat.replace(/\{\{\s*amount[^}]*\}\}/, amount);
      };

      const selectedOptions = () =>
        Array.from(root.querySelectorAll('[data-option-index]:checked, select[data-option-index]')).map(
          (el) => el.value
        );

      const onChange = () => {
        const options = selectedOptions();
        const match = variants.find((variant) =>
          variant.options.every((value, index) => value === options[index])
        );

        if (!match) {
          buyButtons.forEach((button) => {
            button.disabled = true;
            const text = button.querySelector('[data-add-to-cart-text]');
            if (text) text.textContent = button.dataset.unavailableText;
          });
          return;
        }

        if (idInput) idInput.value = match.id;
        priceEls.forEach((el) => {
          el.textContent = formatMoney(match.price);
        });

        comparePriceEls.forEach((el) => {
          if (match.compare_at_price && match.compare_at_price > match.price) {
            el.textContent = formatMoney(match.compare_at_price);
            el.hidden = false;
          } else {
            el.hidden = true;
          }
        });

        buyButtons.forEach((button) => {
          button.disabled = !match.available;
          const text = button.querySelector('[data-add-to-cart-text]');
          if (text) {
            text.textContent = match.available ? button.dataset.addText : button.dataset.soldoutText;
          }
        });

        if (match.featured_media) {
          const mediaTarget = document.querySelector(
            `[data-media-id="${match.featured_media.id}"]`
          );
          if (mediaTarget) {
            mediaTarget.scrollIntoView({
              behavior: reducedMotion.matches ? 'auto' : 'smooth',
              block: 'nearest',
              inline: 'center',
            });
          }
        }

        const url = new URL(window.location.href);
        url.searchParams.set('variant', match.id);
        window.history.replaceState({}, '', url);
      };

      root.querySelectorAll('[data-option-index], select[data-option-index]').forEach((el) => {
        el.addEventListener('change', onChange);
      });
    });
  };

  /* ------------------------------------------------------------ */
  /* Buy bar: shows while the add-to-cart row is out of view       */
  /* ------------------------------------------------------------ */
  const initBuyBar = () => {
    const bar = document.querySelector('[data-buy-bar]');
    const sentinel = document.querySelector('[data-buy-bar-sentinel]');
    if (!bar || !sentinel) return;

    if (!('IntersectionObserver' in window)) return;

    new IntersectionObserver(
      ([entry]) => {
        const show = !entry.isIntersecting;
        bar.classList.toggle('buy-bar--visible', show);
        bar.toggleAttribute('inert', !show);
      },
      { rootMargin: '-64px 0px 0px 0px' }
    ).observe(sentinel);
  };

  /* ------------------------------------------------------------ */
  /* Boot                                                          */
  /* ------------------------------------------------------------ */
  const init = () => {
    initReveals();
    initHeader();
    initHeaderSearch();
    initSubscribePanels();
    initDrawers();
    initQuantities();
    initCart();
    initProductForms();
    initBuyBar();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run inside the theme editor when sections re-render.
  document.addEventListener('shopify:section:load', init);
})();
